import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, map, Observable, take, tap } from 'rxjs';
import {
  GET_MY_CHAT_USER_PATH,
  GET_MY_USER_PATH,
  GetMyChatUserResponse,
  GetMyUserResponse,
  UserRoleEnum
} from '@core-http';
import { ENV_CONFIG, EnvConfig } from '../env';
import { UserModel } from '../interfaces';
import { setSentryTag, setSentryUser } from '@sentry';

const INIT_STATE: UserModel = {
  userId: null,
  email: null,
  phone: null,
  displayName: null,
  name: null,
  surname: null,
  role: null,
  avatarUrl: null,
  isHost: null,
  isPaidHost: null,
  chatSettings: null
} as unknown as UserModel;

@Injectable({ providedIn: 'root' })
export class UserDataService {
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly env: EnvConfig = inject(ENV_CONFIG);

  private readonly userDataSubject: BehaviorSubject<UserModel> = new BehaviorSubject<UserModel>(INIT_STATE);

  readonly userData$: Observable<UserModel> = this.userDataSubject.asObservable();

  setUserData(): void {
    combineLatest([this.getUserData(), this.getChatUserData()])
      .pipe(
        take(1),
        map(([userData, chatSettings]: [GetMyUserResponse, GetMyChatUserResponse]): UserModel => {
          return {
            userId: userData.userId,
            userAuthId: userData.userAuthId,
            email: userData.email,
            phone: userData.phone?.substring(3) ?? '',
            displayName: `${userData.name ?? ''} ${userData.surname ?? ''}`.trim(),
            name: userData.name ?? '',
            surname: userData.surname ?? '',
            role: userData.role as UserRoleEnum,
            avatarUrl: userData.thumbnailUrl ?? '',
            chatSettings: {
              chatUserExternalId: chatSettings?.chatUserExternalId,
              token: chatSettings?.token ?? ''
            },
            playerPreferences: {
              preferredPosition: userData.playerPreferences.preferredPosition,
              preferredLevel: userData.playerPreferences.preferredLevel
            },
            referralToken: userData.referralToken,
            canSetReferral: userData.canSetReferral,
            language: userData.language
          };
        }),
        tap((userData: UserModel) => this.userDataSubject.next(userData)),
        tap((userData: UserModel) => this.setSentryUserContext(userData))
      )
      .subscribe();
  }

  resetUserDataState(): void {
    this.userDataSubject.next(INIT_STATE);
    setSentryUser(null);
  }

  private getUserData(): Observable<GetMyUserResponse> {
    const path: string = GET_MY_USER_PATH;

    return this.httpClient.get<GetMyUserResponse>(`${this.env.apiUrl}${path}`);
  }

  private getChatUserData(): Observable<GetMyChatUserResponse> {
    const path: string = GET_MY_CHAT_USER_PATH;

    return this.httpClient.get<GetMyChatUserResponse>(`${this.env.apiUrl}${path}`);
  }

  private setSentryUserContext(user: UserModel): void {
    setSentryUser({
      id: user.userId,
      email: user.email,
      username: user.displayName
    });

    setSentryTag('user.id', user.userId);
    setSentryTag('user.email', user.email);
  }
}
